"use client";

import React, { useEffect, Component } from "react";
import { Providers } from "@/components/providers";
import { ConfigProvider } from "@/contexts/config-provider";
import { AuthProvider } from "@/contexts/auth-context";
import { Toaster } from "@/components/ui/toaster";
import NextTopLoader from "nextjs-toploader";
import { useServiceWorker } from "@/hooks/use-service-worker";

// Error Boundary Component
class ClientErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background">
          <div className="text-center">
            <h2 className="mb-4 text-2xl font-bold text-destructive">
              Something went wrong
            </h2>
            <p className="mb-4 text-muted-foreground">
              The application encountered an error. Please refresh the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="rounded-md bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

interface ClientLayoutWrapperProps {
  children: React.ReactNode;
  authModal: React.ReactNode;
}

export function ClientLayoutWrapper({
  children,
  authModal,
}: ClientLayoutWrapperProps) {
  // Initialize Service Worker for advanced caching
  useServiceWorker();

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {};
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {};

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection
      );
    };
  }, [children, authModal]);

  return (
    <ClientErrorBoundary>
      <NextTopLoader
        color="#2563eb"
        initialPosition={0.08}
        crawlSpeed={200}
        height={3}
        crawl
        showSpinner={false}
        easing="ease"
        speed={200}
        shadow="0 0 10px #2563eb, 0 0 5px #2563eb"
      />
      <Providers>
        <ConfigProvider>
          <AuthProvider>
            <div className="flex min-h-screen flex-col">
              {children}
              {authModal}
            </div>
          </AuthProvider>
        </ConfigProvider>
      </Providers>
      <Toaster />
    </ClientErrorBoundary>
  );
}
